import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@/lib/api';
import { createSession, getSession, type SessionDetail } from '@/lib/api-client';
import type { ResponseRecord, SpeakerRoleMap, Turn } from '@/lib/types';

// Owns the active session id for one matter and rehydrates the persisted
// transcript (and, for Section D, the persisted responses) on mount. Eagerly
// creates a Session as soon as a matter is selected so the transcript
// persistence has a target before the first ask.

const STORAGE_KEY_PREFIX = 'cc_active_session_id::';
// Courtroom-ready sprint: rehearsal mode. The flag persists across reloads and
// keys its own session cache, so practice sessions and real sessions never mix.
const REHEARSAL_MODE_KEY = 'cc_rehearsal_mode';

export function loadRehearsalMode(): boolean {
  try {
    return window.localStorage.getItem(REHEARSAL_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveRehearsalMode(on: boolean): void {
  try {
    if (on) {
      window.localStorage.setItem(REHEARSAL_MODE_KEY, '1');
    } else {
      window.localStorage.removeItem(REHEARSAL_MODE_KEY);
    }
  } catch {
    // localStorage unavailable; the toggle still works for this tab.
  }
}

export type SessionStatus = 'idle' | 'loading' | 'ready' | 'error';

interface SessionContextValue {
  status: SessionStatus;
  sessionId: string | null;
  /** Initial transcript from the server (or empty when no prior session). */
  initialTranscript: string;
  /** Session start time (ISO) for the feed header, or null when not ready. */
  startedAt: string | null;
  /** Persisted responses for this session, oldest first (feed hydration). */
  initialResponses: ResponseRecord[];
  /** P6 S1: persisted speaker turns + role map for hydration. */
  initialTurns: Turn[];
  initialSpeakerRoles: SpeakerRoleMap;
  error: string | null;
  /** Force a fresh session for the same matter (used after end-session). */
  reset: () => Promise<void>;
  /** Courtroom-ready sprint: rehearsal (practice) mode. Toggling switches to a
   *  separate practice session on the same matter via the identical
   *  production path. */
  rehearsal: boolean;
  setRehearsal: (on: boolean) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  matterId: string | null;
  children: ReactNode;
}

export function SessionProvider({ matterId, children }: SessionProviderProps) {
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [initialTranscript, setInitialTranscript] = useState<string>('');
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [initialResponses, setInitialResponses] = useState<ResponseRecord[]>([]);
  const [initialTurns, setInitialTurns] = useState<Turn[]>([]);
  const [initialSpeakerRoles, setInitialSpeakerRoles] = useState<SpeakerRoleMap>({});
  const [error, setError] = useState<string | null>(null);
  const [rehearsal, setRehearsalState] = useState<boolean>(loadRehearsalMode);
  const matterIdRef = useRef<string | null>(null);
  const rehearsalRef = useRef(rehearsal);
  rehearsalRef.current = rehearsal;

  async function ensureSession(
    forMatter: string,
    options: { fresh?: boolean } = {},
  ): Promise<void> {
    setStatus('loading');
    setError(null);
    setInitialTranscript('');
    setStartedAt(null);
    setInitialResponses([]);
    setInitialTurns([]);
    setInitialSpeakerRoles({});
    try {
      const wantRehearsal = rehearsalRef.current;
      const cachedId = options.fresh ? null : readCachedId(forMatter, wantRehearsal);
      if (cachedId) {
        try {
          const detail = await getSession(cachedId);
          if (
            detail.matterId === forMatter &&
            !detail.endedAt &&
            (detail.isRehearsal ?? false) === wantRehearsal
          ) {
            setSessionId(detail.id);
            setInitialTranscript(detail.transcript ?? '');
            setStartedAt(detail.startedAt);
            setInitialResponses(detail.responses ?? []);
            setInitialTurns(detail.turns ?? []);
            setInitialSpeakerRoles(detail.speakerRoles ?? {});
            setStatus('ready');
            return;
          }
        } catch (err) {
          // If the cached id is stale (404) or anything else, fall through
          // and create a fresh session.
          if (!(err instanceof ApiError) || err.status !== 404) {
            console.warn('cached session restore failed:', err);
          }
        }
      }
      const created = await createSession(forMatter, { isRehearsal: wantRehearsal });
      writeCachedId(forMatter, created.id, wantRehearsal);
      setSessionId(created.id);
      setInitialTranscript('');
      setStartedAt(created.startedAt);
      setInitialResponses([]);
      setStatus('ready');
    } catch (err) {
      setSessionId(null);
      setInitialTranscript('');
      setStartedAt(null);
      setInitialResponses([]);
      setError(formatError(err));
      setStatus('error');
    }
  }

  useEffect(() => {
    if (matterIdRef.current === matterId) {
      return;
    }
    matterIdRef.current = matterId;
    if (!matterId) {
      setStatus('idle');
      setSessionId(null);
      setInitialTranscript('');
      setStartedAt(null);
      setInitialResponses([]);
      setError(null);
      return;
    }
    void ensureSession(matterId);
  }, [matterId]);

  const value: SessionContextValue = {
    status,
    sessionId,
    initialTranscript,
    startedAt,
    initialResponses,
    initialTurns,
    initialSpeakerRoles,
    error,
    reset: async () => {
      if (!matterIdRef.current) {
        return;
      }
      clearCachedId(matterIdRef.current, rehearsalRef.current);
      await ensureSession(matterIdRef.current, { fresh: true });
    },
    rehearsal,
    setRehearsal: (on: boolean) => {
      if (on === rehearsalRef.current) {
        return;
      }
      saveRehearsalMode(on);
      setRehearsalState(on);
      rehearsalRef.current = on;
      if (matterIdRef.current) {
        void ensureSession(matterIdRef.current);
      }
    },
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return ctx;
}

// --- localStorage helpers ---------------------------------------------------

function storageKey(matterId: string, rehearsal: boolean): string {
  return rehearsal ? `${STORAGE_KEY_PREFIX}rehearsal::${matterId}` : `${STORAGE_KEY_PREFIX}${matterId}`;
}

function readCachedId(matterId: string, rehearsal: boolean): string | null {
  try {
    return window.localStorage.getItem(storageKey(matterId, rehearsal));
  } catch {
    return null;
  }
}

function writeCachedId(matterId: string, sessionId: string, rehearsal: boolean): void {
  try {
    window.localStorage.setItem(storageKey(matterId, rehearsal), sessionId);
  } catch {
    // localStorage may be unavailable. The session works without restore.
  }
}

function clearCachedId(matterId: string, rehearsal: boolean): void {
  try {
    window.localStorage.removeItem(storageKey(matterId, rehearsal));
  } catch {
    // ignore
  }
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message;
  }
  return err instanceof Error ? err.message : 'Could not start session';
}

// Re-export so consumers don't have to dual-import.
export type { SessionDetail };
